import React, { useContext } from "react";
import { chunk, map } from "lodash";
import classNames from "classnames";

import { NavigationItem } from "components/channels/widgets/widgetsFields/navigationWidget/NavigationItem";
import { getWidgetContentColumns } from "components/channels/helpers";
import { WidgetContext } from "components/channels/widgets/WidgetContainer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ICONS } from "components/shared/form/fields/IconPicker";
import "./widgets.css";

type NavigationWidgetArgs = {
  navigation_type: "buttons" | "list" | "grid";
  background_override: string;
  text_override: string;
  navigation_items: NavigationItem[];
};

export default function NavigationWidget({
  navigation_items,
  navigation_type,
  ...props
}: NavigationWidgetArgs) {
  const { w } = useContext(WidgetContext);

  let NavigationComponent;
  if (navigation_type === "buttons") {
    NavigationComponent = ButtonNavigation;
  } else if (navigation_type === "grid") {
    NavigationComponent = GridNavigation;
  } else {
    NavigationComponent = ListNavigation;
  }

  return <NavigationComponent items={navigation_items} w={w} {...props} />;
}

function GridNavigation({
  items,
  background_override,
  text_override,
  w,
}: {
  items: NavigationItem[];
  background_override: string;
  text_override: string;
  w: number;
}) {
  return (
    <div className="p-6">
      <div
        className={classNames("grid gap-4 h-full", {
          "grid-cols-2": w === 1,
          "grid-cols-2 sm:grid-cols-4": w === 2,
          "grid-cols-2 sm:grid-cols-6": w === 3,
        })}
      >
        {items &&
          map(items.slice(0, w * 6), (item, index) => {
            const tileStyle = {};

            if (background_override || item.background_override) {
              tileStyle["backgroundColor"] =
                item.background_override || background_override;
            }
            if (text_override || item.text_override) {
              tileStyle["color"] = item.text_override || text_override;
            }

            return (
              <LinkItem
                key={index}
                type={item.type}
                url={item.url}
                newWindow={item.new_window}
                widget={item.widget}
                className="navigation-widget-grid-tile flex flex-col items-center justify-center p-4 rounded border text-center h-24"
                style={tileStyle}
              >
                {item.icon && (
                  <FontAwesomeIcon
                    className="text-2xl mb-2"
                    icon={ICONS[item.icon]}
                  />
                )}
                <div className="text-sm font-medium">{item.title}</div>
              </LinkItem>
            );
          })}
      </div>
    </div>
  );
}

function ButtonNavigation({
  items,
  background_override,
  text_override,
  w,
}: {
  items: NavigationItem[];
  background_override: string;
  text_override: string;
  w: number;
}) {
  return (
    <div
      className={classNames(
        "grid gap-4 h-full p-6",
        getWidgetContentColumns(w),
      )}
    >
      {items &&
        map(chunk(items.slice(0, w * 6), 6), (colItems, index) => (
          <div
            className={classNames("flex flex-col gap-3", {
              "justify-center": w === 1,
            })}
            key={index}
          >
            {map(colItems, (item, index) => {
              const buttonStyle = {};

              if (background_override || item.background_override) {
                buttonStyle["backgroundColor"] =
                  item.background_override || background_override;
              }
              if (text_override || item.text_override) {
                buttonStyle["color"] = item.text_override || text_override;
              }

              return (
                <LinkItem
                  key={index}
                  type={item.type}
                  url={item.url}
                  newWindow={item.new_window}
                  className="navigation-widget-button"
                  style={buttonStyle}
                  widget={item.widget}
                >
                  <>
                    {item.icon && (
                      <FontAwesomeIcon
                        className="mr-1"
                        icon={ICONS[item.icon]}
                      />
                    )}
                    {item.title}
                  </>
                </LinkItem>
              );
            })}
          </div>
        ))}
    </div>
  );
}

function ListNavigation({
  items,
  background_override,
  text_override,
  w,
}: {
  items: NavigationItem[];
  w: number;
  background_override: string;
  text_override: string;
}) {
  return (
    <div
      className={classNames(
        "grid gap-4 h-full p-6",
        getWidgetContentColumns(w),
      )}
    >
      {items &&
        map(chunk(items.slice(0, w * 6), 6), (colItems, index) => (
          <ul className="navigation-widget-list" key={index}>
            {map(colItems, (item, index) => (
              <li
                key={index}
                style={
                  background_override || item.background_override
                    ? { color: item.background_override || background_override }
                    : undefined
                }
              >
                <LinkItem
                  type={item.type}
                  url={item.url}
                  className="navigation-widget-link"
                  newWindow={item.new_window}
                  widget={item.widget}
                  style={
                    text_override || item.text_override
                      ? { color: item.text_override || text_override }
                      : undefined
                  }
                >
                  <>{item.title}</>
                </LinkItem>
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
}

function LinkItem({
  type,
  url,
  newWindow,
  style,
  children,
  className,
  widget,
}: {
  type: string;
  url?: string;
  newWindow?: boolean;
  style?: React.CSSProperties;
  children: React.ReactNode;
  className?: string;
  widget?: { [key: string]: string };
}) {
  const scrollWidgetIntoView = (widgetId?: string) => {
    if (!widgetId) return;

    document.getElementById(`widget-${widgetId}`)?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  };

  switch (type) {
    case "widget":
      return (
        <button
          className={className}
          style={style}
          onClick={() => scrollWidgetIntoView(widget?.id)}
        >
          {children}
        </button>
      );
    case "link":
    default:
      return (
        <a
          href={url}
          target={newWindow ? "_blank" : undefined}
          className={className}
          rel={newWindow ? "noopener noreferrer" : undefined}
          style={style}
        >
          {children}
        </a>
      );
  }
}
